import { isAxiosError } from 'axios';
import { useCallback, useMemo, useState } from 'react';

import { StatusBar } from '@/policy/component/StatusBar/StatusBar';
import { useValidation } from '@/policy/contexts';
import { useCompletePolicy } from '@/policy/hooks/useCompletePolicy';
import { usePausePolicy } from '@/policy/hooks/usePausePolicy';

import { PolicyAssignmentConflictDialog } from '../Dialogs/PolicyAssignmentConflictDialog';

interface PolicyStatusBarProps {
  uuid: string;
  totalSections: number;
  onSuccess: () => void;
  onSaveAndClose: () => void;
}

export const PolicyStatusBar = ({
  uuid,
  totalSections,
  onSuccess,
  onSaveAndClose,
}: PolicyStatusBarProps) => {
  const [showConflictDialog, setShowConflictDialog] = useState(false);

  const { validations, allValidations } = useValidation();
  const { pausePolicy, isPausing } = usePausePolicy();

  const handleAssignmentConflict = useCallback((error: unknown) => {
    if (
      isAxiosError(error) &&
      error?.response?.data?.code === 'POLICY_ASSIGNMENT_CONFLICT'
    ) {
      setShowConflictDialog(true);
    }
  }, []);

  const { completePolicy, isCompleting } = useCompletePolicy({
    onSuccess: onSuccess,
    onError: handleAssignmentConflict,
  });

  const handleSaveAndClose = useCallback(async () => {
    try {
      await pausePolicy({ uuid, validations });
      onSaveAndClose();
    } catch (error) {
      handleAssignmentConflict(error);
    }
  }, [
    pausePolicy,
    uuid,
    validations,
    onSaveAndClose,
    handleAssignmentConflict,
  ]);

  const handleSubmit = useCallback(() => {
    completePolicy({ uuid, validations });
  }, [completePolicy, uuid, validations]);

  const progress = useMemo(() => {
    const validated = allValidations.filter(
      (validation) =>
        validation.status === 'COHERENT' ||
        (validation.status === 'ERROR' && validation.confirmed),
    ).length;
    const errors = allValidations.filter(
      (validation) => validation.status === 'ERROR' && validation.confirmed,
    ).length;

    return {
      validated,
      total: totalSections,
      errors,
    };
  }, [allValidations, totalSections]);

  const canSubmit =
    allValidations.length >= totalSections &&
    allValidations.every(
      (validation) =>
        validation.status === 'COHERENT' ||
        (validation.status === 'ERROR' && validation.confirmed),
    );

  return (
    <>
      <StatusBar
        onSaveAndClose={handleSaveAndClose}
        isSaving={isPausing}
        progress={progress}
        onSubmit={handleSubmit}
        isSubmitting={isCompleting}
        canSubmit={canSubmit}
      />
      <PolicyAssignmentConflictDialog
        open={showConflictDialog}
        onClose={() => {
          setShowConflictDialog(false);
          onSaveAndClose();
        }}
      />
    </>
  );
};
